import {
    Box,
    Flex,
    Icon,
    Spinner,
    Table,
    Tbody,
    Td,
    Text,
    Th,
    Thead,
    Tr,
    useColorModeValue
} from '@chakra-ui/react';
import {
    createColumnHelper,
    flexRender,
    getCoreRowModel,
    getFilteredRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    useReactTable,
} from '@tanstack/react-table';
import Card from "components/card/Card";
import CButton from "components/CButton";
import CMenu from 'components/CMenu';
import ConfirmDialog from "components/ConfirmDialog";
import { SearchBar } from 'components/navbar/searchBar/SearchBar';
import Pagination from "components/Pagination";
import { useMemberGroup } from 'contexts/MemberGroupContext';
import { useReport } from 'contexts/ReportContext';
import { useEffect, useMemo, useState } from "react";
import { FaRegEdit } from "react-icons/fa";
import { RiDeleteBin6Line } from "react-icons/ri";
import { useNavigate } from "react-router-dom";

export default function GroupMembersOverview({ canCreate, canRead, canUpdate, canDelete }) {
    const { getAllMemberGroup, deleteMemberGroup } = useMemberGroup();
    const { memberLockReportCSV } = useReport();

    const navigate = useNavigate();

    const textColor = useColorModeValue('navy.700', 'white');

    const [globalFilter, setGlobalFilter] = useState('');
    const [data, setData] = useState([]);
    const [totalPages, setTotalPages] = useState(0);
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [userToDelete, setUserToDelete] = useState(null);
    const [filter, setFilter] = useState({
        search: '',
        limit: 10,
        page: 1
    });

    const handleDelete = async () => {
        if (userToDelete) {
            try {
                await deleteMemberGroup(userToDelete);
                setUserToDelete(null);
                fetchGroupMembers();
            } catch (error) {
                console.error("Failed to delete user:", error);
            }
            setIsDialogOpen(false);
        }
    };

    const openDeleteDialog = (userId) => {
        setUserToDelete(userId);
        setIsDialogOpen(true);
    };


    const columnHelper = createColumnHelper();
    const columns = useMemo(() => {
        const baseColumns = [
            columnHelper.accessor('id', {
                header: () => <Text color="gray.400">Group ID</Text>,
                cell: (info) => (
                    <Text color={textColor} fontSize="md" fontWeight="500">
                        {info.getValue()}
                    </Text>
                ),
            }),
            columnHelper.accessor('name', {
                header: () => <Text color="gray.400">GROUP NAME</Text>,
                cell: (info) => (
                    <Text color={textColor} fontSize="md" fontWeight="500">
                        {info.getValue()}
                    </Text>
                ),
            }),
            columnHelper.accessor('memberCount', {
                header: () => <Text color="gray.400">MEMBER COUNT</Text>,
                cell: (info) => (
                    <Text color={textColor} fontSize="md" fontWeight="500">
                        {info.getValue()}
                    </Text>
                ),
            }),
        ]

        if (canUpdate || canDelete) {
            baseColumns.push(
                columnHelper.accessor('actions', {
                    header: () => <Text color="gray.400">ACTIONS</Text>,
                    cell: (info) => (
                        <Flex>
                            {canUpdate && <Icon
                                as={FaRegEdit}
                                h={6}
                                w={6}
                                mt='10px'
                                me='20px'
                                onClick={() => navigate(`/admin/member/groups/edit/${info.row.original.id}`)}
                                cursor='pointer'
                                title="Edit"
                                color='#fa8f03'
                            />}
                            {canDelete && <Icon
                                as={RiDeleteBin6Line}
                                h={6}
                                w={6}
                                mt='10px'
                                me='20px'
                                onClick={() => openDeleteDialog(info.row.original.id)}
                                cursor='pointer'
                                title="Delete"
                                color='#fa8f03'
                            />}
                        </Flex>
                    ),
                }),
            )
        }
        return baseColumns;
    }, [canUpdate, canDelete]);

    const [{ pageIndex, pageSize }, setPagination] = useState({
        pageIndex: 0,
        pageSize: 10,
    });

    const pagination = useMemo(
        () => ({ pageIndex, pageSize }),
        [pageIndex, pageSize],
    );

    const table = useReactTable({
        data,
        columns,
        state: { globalFilter, pagination },
        onGlobalFilterChange: setGlobalFilter,
        onPaginationChange: setPagination,
        getCoreRowModel: getCoreRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
    });

    useEffect(() => {
        fetchGroupMembers();
    }, [filter.search, filter.limit, filter.page]);

    const fetchGroupMembers = async () => {
        setIsLoading(true);
        try {
            const data = await getAllMemberGroup({
                search: filter.search,
                limit: filter.limit,
                page: filter.page,
            });

            if (data && data.groups) {
                setData(data.groups);
                setTotalPages(data.totalPages || 0)
            } else {
                console.warn("No Group Members found or data is undefined");
                setData([]);
            }
        } catch (error) {
            console.error("Failed to fetch Group Members:", error);
            setData([]);
        } finally {
            setIsLoading(false);
        }
    };

    const handlePageChange = (newPageIndex) => {
        setPagination((prev) => ({
            ...prev,
            pageIndex: newPageIndex,
        }));
        setFilter((prev) => ({
            ...prev,
            page: newPageIndex,
        }));
    };

    const handleReportApi = async (type, payload) => {
        if (type === 'csv') {
            const data = await memberLockReportCSV(payload);
            if (data && !data.error) {
                window.open(data.data, '_blank');
            }
        }
    };

    const handleRangeSelect = (fileType, range) => {
        if (range !== 'custom') {
            handleReportApi(fileType, range);
        }
    };

    const handleCustomDateSubmit = (fileType, startDate, endDate) => {
        handleReportApi(fileType, 'custom', { startDate, endDate });
    };

    const handleMemberGroupSubmit = (fileType, memberGroupId) => {
        handleReportApi(fileType, 'memberGroup', { memberGroupId });
    }

    const handleUnifiedSubmit = ({ fileType, filter, startDate = null, endDate = null, memberGroupId = null }) => {
        const payload = { filter };

        if (startDate && endDate) {
            payload.startDate = startDate;
            payload.endDate = endDate;
        }

        if (memberGroupId) {
            payload.memberGroupId = memberGroupId;
        }

        handleReportApi(fileType, payload);
    };

    return (
        <Flex direction='column' pt={{ sm: "100px", lg: "55px" }}>
            <ConfirmDialog
                position='top'
                isOpen={isDialogOpen}
                onClose={() => setIsDialogOpen(false)}
                onConfirm={handleDelete}
                header="Delete Confirmation"
                message="Are you sure you want to delete this Group?"
            />
            <Card px='0px' minHeight="80vh">
                <Flex direction="column" w="100%" overflowX="auto">
                    <Flex align="center" justify="space-between" w="100%" px="22px" mb="36px">
                        <DebouncedInput
                            value={globalFilter ?? ''}
                            onChange={(value) => setFilter((prev) => ({ ...prev, search: value, page: 1 }))}
                            placeholder="Search..."
                        />
                        <Flex gap="16px">
                            <CMenu
                                label="CSV"
                                fileType="csv"
                                isMemberGroupMenu={true}
                                onRangeSelect={handleRangeSelect}
                                onCustomDateSubmit={handleCustomDateSubmit}
                                onMemberGroupSubmit={handleMemberGroupSubmit}
                                onUnifiedSubmit={handleUnifiedSubmit}
                                useUnifiedSubmit={true}
                            />
                            {canCreate &&
                                <Flex gap='16px'>
                                    <CButton
                                        onClick={() => navigate('/admin/member/groups/create')}
                                    >
                                        Add Group
                                    </CButton>
                                </Flex>
                            }
                        </Flex>
                    </Flex>
                    {isLoading ? (
                        <Box
                            display="flex"
                            justifyContent="center"
                            alignItems="center"
                            borderRadius="10px"
                            overflow="hidden"
                            minHeight="500px"
                        >
                            <Spinner color="#fa8f03" />
                        </Box>
                    ) : (
                        <>
                            <Box border="2px solid #fa8f03" borderRadius="10px" overflow="auto" maxHeight="520px">
                                <Table variant="simple">
                                    <Thead>
                                        {table.getHeaderGroups().map((headerGroup) => (
                                            <Tr key={headerGroup.id} bg='#fa8f03'>
                                                {headerGroup.headers.map((header) => (
                                                    <Th key={header.id} borderColor='#fa8f03'>
                                                        {header.isPlaceholder ? null : (
                                                            <Flex
                                                                onClick={header.column.getToggleSortingHandler()}
                                                                cursor={header.column.getCanSort() ? 'pointer' : 'default'}
                                                                justify="space-between"
                                                                align="center"
                                                                color="white"
                                                            >
                                                                {flexRender(header.column.columnDef.header, header.getContext())}
                                                            </Flex>
                                                        )}
                                                    </Th>
                                                ))}
                                            </Tr>
                                        ))}
                                    </Thead>
                                    <Tbody>
                                        {data.length > 0 ? (
                                            table.getRowModel().rows.map((row) => (
                                                <Tr key={row.id}>
                                                    {row.getVisibleCells().map((cell) => (
                                                        <Td key={cell.id} fontSize="sm" borderColor='#fa8f03'>
                                                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                                        </Td>
                                                    ))}
                                                </Tr>
                                            ))
                                        ) : (
                                            <Tr>
                                                <Td colSpan={columns.length} textAlign="center">
                                                    No Groups found.
                                                </Td>
                                            </Tr>
                                        )}
                                    </Tbody>
                                </Table>
                            </Box>
                            <Pagination totalPages={totalPages} currentPage={filter.page} onPageChange={handlePageChange} />
                        </>)}
                </Flex>
            </Card>
        </Flex>
    );
}

function DebouncedInput({ value: initialValue, onChange, debounce = 500, ...props }) {
    const [value, setValue] = useState(initialValue);

    useEffect(() => {
        setValue(initialValue);
    }, [initialValue]);

    useEffect(() => {
        const timeout = setTimeout(() => {
            onChange(value);
        }, debounce);
        return () => clearTimeout(timeout);
    }, [value, debounce]);

    return (
        <SearchBar
            value={value}
            onChange={(e) => setValue(e.target.value)}
            h="44px"
            w={{ lg: '390px' }}
            borderRadius="16px"
            {...props}
        />
    );
}
